import { useCallback, useEffect, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { useDispatch } from "react-redux";

import { db } from "../../../../config/firebase";
import { saveBooking } from "../../../bookings/booking/bookingsThunk";
import {
  buildBookingConfirmationEmailContent,
  createBookingConfirmationPdf,
} from "../../../../utils/bookingConfirmation";
import { downloadFilePayload } from "../../../../utils/download";
import "../BookingsList/BookingsList.css";

const ensurePlainObject = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const mapPendingBooking = (docSnapshot) => {
  const data = ensurePlainObject(docSnapshot.data());
  const booking = ensurePlainObject(data.booking);
  const availability = ensurePlainObject(data.availability);
  const payment = ensurePlainObject(data.payment);
  const resolution = ensurePlainObject(data.resolution);

  return {
    id: docSnapshot.id,
    booking,
    availability,
    status: data.status || resolution.status || "pending",
    payment,
    resolution,
    finalizedAt: data.finalizedAt || null,
    createdAt: data.createdAt || null,
  };
};

const deriveResolutionInfo = (pending) => {
  const rawStatuses = [
    pending?.resolution?.status,
    pending?.status,
    pending?.payment?.status,
  ]
    .map((status) =>
      typeof status === "string" ? status.trim().toLowerCase() : null
    )
    .filter(Boolean);

  const rawStatus = rawStatuses[0] || "pending";

  if (["accepted", "confirm", "confirmed", "success"].includes(rawStatus)) {
    return {
      category: "accepted",
      label: "Accepted",
      metaPrefix: "Accepted at:",
    };
  }

  if (["declined", "rejected", "cancelled", "canceled"].includes(rawStatus)) {
    return {
      category: "declined",
      label: "Declined",
      metaPrefix: "Declined at:",
    };
  }

  return { category: "pending", label: "Pending", metaPrefix: "" };
};

const getResolutionTimestamp = (pending) => {
  if (!pending) {
    return null;
  }

  const resolution = ensurePlainObject(pending.resolution);
  const paymentMetadata = ensurePlainObject(pending.payment?.metadata);

  return (
    resolution.finalizedAt ||
    resolution.resolvedAt ||
    resolution.completedAt ||
    pending.finalizedAt ||
    pending.acceptedAt ||
    pending.declinedAt ||
    pending.updatedAt ||
    paymentMetadata.confirmedAt ||
    paymentMetadata.resolvedAt ||
    null
  );
};

function PendingBookingsList() {
  const dispatch = useDispatch();
  const [pendingBookings, setPendingBookings] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionState, setActionState] = useState({});
  const [downloadState, setDownloadState] = useState({});

  const fetchPendingBookings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const pendingQuery = query(
        collection(db, "pendingBookings"),
        orderBy("createdAt", "asc")
      );
      const snapshot = await getDocs(pendingQuery);

      const results = snapshot.docs.map(mapPendingBooking);

      setPendingBookings(results);
    } catch (fetchError) {
      console.error("Error fetching pending bookings:", fetchError);
      setError("Unable to load pending bookings. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingBookings();
  }, [fetchPendingBookings]);

  const resetActionState = (pendingId) => {
    setActionState((previous) => {
      const updated = { ...previous };
      delete updated[pendingId];
      return updated;
    });
  };

  const deleteAssociatedTokens = async (pendingId) => {
    try {
      const tokensSnapshot = await getDocs(
        query(
          collection(db, "pendingBookingTokens"),
          where("pendingBookingId", "==", pendingId)
        )
      );

      if (tokensSnapshot.empty) {
        return;
      }

      await Promise.all(
        tokensSnapshot.docs.map((tokenDoc) => deleteDoc(tokenDoc.ref))
      );
    } catch (tokenError) {
      console.error("Failed to delete pending booking tokens", tokenError);
    }
  };

  const refreshPendingEntry = useCallback(async (pendingId) => {
    try {
      const pendingRef = doc(db, "pendingBookings", pendingId);
      const snapshot = await getDoc(pendingRef);

      if (snapshot.exists()) {
        const updatedEntry = mapPendingBooking(snapshot);
        setPendingBookings((previous) =>
          previous.map((pending) =>
            pending.id === pendingId ? updatedEntry : pending
          )
        );
      } else {
        setPendingBookings((previous) =>
          previous.filter((pending) => pending.id !== pendingId)
        );
      }
    } catch (refreshError) {
      console.error("Failed to refresh pending booking", refreshError);
    }
  }, []);

  const handleAccept = async (pending) => {
    const pendingId = pending.id;
    setError(null);
    setActionState((previous) => ({ ...previous, [pendingId]: "accept" }));

    try {
      const bookingDetails = ensurePlainObject(pending.booking);
      const availabilityDetails = ensurePlainObject(pending.availability);

      const normalizedBookingDetails = {
        ...bookingDetails,
        totalPrice: Number(bookingDetails.totalPrice) || 0,
      };

      const normalizedAvailabilityDetails = {
        ...availabilityDetails,
        people: Number(availabilityDetails.people) || 0,
      };

      const bookingPayload = {
        email: normalizedBookingDetails.email || "",
        cellphone: normalizedBookingDetails.cellphone || "",
        country: normalizedBookingDetails.country || "",
        name: normalizedBookingDetails.name || "",
        totalPrice: normalizedBookingDetails.totalPrice,
        date: normalizedAvailabilityDetails.date || "",
        session: normalizedAvailabilityDetails.session || "",
        people: normalizedAvailabilityDetails.people,
        pickupPoint: normalizedAvailabilityDetails.pickupPoint || "",
        payment: {
          status: pending.payment?.status || "manual",
          metadata: {
            ...ensurePlainObject(pending.payment?.metadata),
            confirmedBy: "business-dashboard",
            confirmedAt: new Date().toISOString(),
            pendingBookingId: pendingId,
            resolution: "accepted",
          },
        },
      };

      const savedBooking = await dispatch(saveBooking(bookingPayload)).unwrap();

      const pendingRef = doc(db, "pendingBookings", pendingId);
      await updateDoc(pendingRef, {
        status: "confirmed",
        bookingId: savedBooking?.id || null,
        finalizedAt: serverTimestamp(),
        booking: normalizedBookingDetails,
        availability: normalizedAvailabilityDetails,
        payment: bookingPayload.payment,
        resolution: {
          status: "accepted",
          finalizedBy: "business-dashboard",
          finalizedAt: serverTimestamp(),
        },
      });

      await deleteAssociatedTokens(pendingId);

      const formattedDate = formatDate(normalizedAvailabilityDetails.date);
      const totalPriceDisplay = formatPrice(bookingPayload.totalPrice);

      const documentDetails = {
        guestName: bookingPayload.name,
        bookingDateIso: normalizedAvailabilityDetails.date,
        pickupPointDisplay:
          normalizedAvailabilityDetails.pickupPoint ||
          bookingPayload.pickupPoint,
        totalPriceValue: bookingPayload.totalPrice,
      };

      const emailContent = buildBookingConfirmationEmailContent(
        documentDetails
      );

      const customerEmail = {
        to: bookingPayload.email,
        message: {
          subject: "Booking Confirmation - Yavola Tours",
          html: emailContent.html,
          text: emailContent.text,
        },
      };

      if (customerEmail.to) {
        try {
          const pdfAttachment = await createBookingConfirmationPdf(
            documentDetails
          );

          const base64Content =
            typeof pdfAttachment?.base64 === "string"
              ? pdfAttachment.base64.trim()
              : "";

          if (base64Content) {
            const attachments = [
              {
                filename: pdfAttachment.fileName,
                content: base64Content,
                type: "application/pdf",
                disposition: "attachment",
                encoding: "base64",
              },
            ];

            customerEmail.message.attachments = attachments;
            customerEmail.attachments = attachments;
          }
        } catch (pdfError) {
          console.error(
            "Failed to generate booking confirmation PDF for email",
            pdfError
          );
        }
      }

      const ownerEmail = {
        to: "bookings@yavolatours.com",
        message: {
          subject: "Pending booking approved",
          html: `<p>Hello team,</p>
                 <p>${bookingPayload.name || "A guest"} has been manually confirmed for ${formattedDate}.</p>
                 <p>Session: ${bookingPayload.session || "Not specified"}</p>
                 <p>Pickup Point: ${bookingPayload.pickupPoint || "Not provided"}</p>
                 <p>Total Price: ${totalPriceDisplay}</p>`,
        },
      };

      try {
        const mailCollection = collection(db, "mail");
        if (customerEmail.to) {
          await addDoc(mailCollection, customerEmail);
        }
        await addDoc(mailCollection, ownerEmail);
      } catch (mailError) {
        console.error("Failed to queue confirmation emails", mailError);
      }
      await refreshPendingEntry(pendingId);
    } catch (acceptError) {
      console.error("Failed to accept pending booking", acceptError);
      setError(
        "We couldn't confirm that booking. Please refresh and try again."
      );
    } finally {
      resetActionState(pendingId);
    }
  };

  const handleDecline = async (pending) => {
    const pendingId = pending.id;
    setError(null);
    setActionState((previous) => ({ ...previous, [pendingId]: "decline" }));

    try {
      const pendingRef = doc(db, "pendingBookings", pendingId);
      await updateDoc(pendingRef, {
        status: "declined",
        finalizedAt: serverTimestamp(),
        resolution: {
          status: "declined",
          finalizedBy: "business-dashboard",
          finalizedAt: serverTimestamp(),
        },
      });

      await deleteAssociatedTokens(pendingId);
      await refreshPendingEntry(pendingId);
    } catch (declineError) {
      console.error("Failed to decline pending booking", declineError);
      setError("We couldn't decline that booking. Please try again.");
    } finally {
      resetActionState(pendingId);
    }
  };

  const handleDownloadDocument = async (pending) => {
    if (!pending?.id) {
      return;
    }

    const pendingId = pending.id;
    setDownloadState((previous) => ({ ...previous, [pendingId]: true }));

    try {
      const documentDetails = {
        guestName: pending.booking?.name,
        bookingDateIso:
          pending.availability?.date || pending.booking?.date || "",
        pickupPointDisplay:
          pending.availability?.pickupPoint ||
          pending.booking?.pickupPoint ||
          "",
        totalPriceValue:
          typeof pending.booking?.totalPrice === "number"
            ? pending.booking.totalPrice
            : Number(pending.booking?.totalPrice),
      };

      const pdf = await createBookingConfirmationPdf(documentDetails);

      downloadFilePayload({
        blob: pdf?.blob,
        base64: pdf?.base64,
        fileName: pdf?.fileName,
        mimeType: "application/pdf",
      });
    } catch (downloadError) {
      console.error(
        "Failed to download booking confirmation PDF",
        downloadError
      );
    } finally {
      setDownloadState((previous) => {
        const nextState = { ...previous };
        delete nextState[pendingId];
        return nextState;
      });
    }
  };

  return (
    <div className="bookingsListContainer">
      <div className="bookingsListHeader">
        <h2>Pending bookings</h2>
        <p className="bookingsListSubhead">
          Approve reservations as soon as payment clears or decline unavailable
          slots.
        </p>
      </div>

      {isLoading && (
        <div className="bookingsLoadingState" role="status" aria-live="polite">
          <span className="bookingsSpinner" aria-hidden="true" />
          <span className="bookingsLoadingMessage">Loading pending bookings…</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="bookingsErrorState" role="alert">
          {error}
        </div>
      )}

      {!isLoading && !error && pendingBookings.length === 0 && (
        <div className="bookingsEmptyState">
          <h3>No pending bookings</h3>
          <p>Great news! There are no bookings awaiting approval.</p>
        </div>
      )}

      {!isLoading && !error && pendingBookings.length > 0 && (
        <div className="bookingsTableWrapper">
          <table className="bookingsTable">
            <thead>
              <tr>
                <th scope="col">Date</th>
                <th scope="col">Session</th>
                <th scope="col">Pickup point</th>
                <th scope="col">Guests</th>
                <th scope="col">Guest name</th>
                <th scope="col">Email</th>
                <th scope="col">Documents</th>
                <th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {pendingBookings.map((pending) => {
                const status = actionState[pending.id];
                const isProcessing = Boolean(status);
                const resolutionInfo = deriveResolutionInfo(pending);
                const resolutionTimestamp = getResolutionTimestamp(pending);
                const isPendingStatus = resolutionInfo.category === "pending";
                return (
                  <tr key={pending.id}>
                    <td data-title="Date">
                      {formatDate(pending.availability?.date)}
                    </td>
                    <td data-title="Session">
                      {pending.availability?.session || "—"}
                    </td>
                    <td data-title="Pickup point">
                      {pending.availability?.pickupPoint || "—"}
                    </td>
                    <td data-title="Guests">
                      {pending.availability?.people ?? "—"}
                    </td>
                    <td data-title="Guest name">
                      {pending.booking?.name || "—"}
                    </td>
                    <td data-title="Email">{pending.booking?.email || "—"}</td>
                    <td data-title="Documents">
                      <button
                        type="button"
                        className="bookingDocumentButton"
                        onClick={() => handleDownloadDocument(pending)}
                        disabled={Boolean(downloadState[pending.id])}
                      >
                        {downloadState[pending.id]
                          ? "Preparing…"
                          : "Download PDF"}
                      </button>
                    </td>
                    <td data-title="Status">
                      {isPendingStatus ? (
                        <div className="bookingsActionGroup">
                          <button
                            type="button"
                            className="bookingActionButton bookingActionButton--accept"
                            onClick={() => handleAccept(pending)}
                            disabled={isProcessing}
                          >
                            {status === "accept" ? "Accepting…" : "Accept"}
                          </button>
                          <button
                            type="button"
                            className="bookingActionButton bookingActionButton--decline"
                            onClick={() => handleDecline(pending)}
                            disabled={isProcessing}
                          >
                            {status === "decline" ? "Declining…" : "Decline"}
                          </button>
                        </div>
                      ) : (
                        <div
                          className={`bookingStatus bookingStatus--${resolutionInfo.category}`}
                        >
                          <span
                            className={`bookingStatus__badge bookingStatus__badge--${resolutionInfo.category}`}
                          >
                            {resolutionInfo.label}
                          </span>
                          {resolutionTimestamp && resolutionInfo.metaPrefix && (
                            <span className="bookingStatus__meta">
                              {`${resolutionInfo.metaPrefix} ${formatTimestamp(
                                resolutionTimestamp
                              )}`}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function formatDate(dateString) {
  if (!dateString) {
    return "—";
  }

  try {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch (error) {
    console.error("Error formatting date:", dateString, error);
  }

  return "—";
}

function formatPrice(value) {
  const numericValue =
    typeof value === "number"
      ? value
      : typeof value === "string"
      ? Number(value)
      : Number.NaN;

  if (Number.isNaN(numericValue)) {
    return "N/A";
  }

  try {
    const formatted = new Intl.NumberFormat(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
    return `N$ ${formatted}`;
  } catch (error) {
    console.error("Error formatting price", value, error);
    return `N$ ${numericValue}`;
  }
}

function formatTimestamp(timestamp) {
  if (!timestamp) {
    return "—";
  }

  try {
    let date;

    if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp.toDate === "function") {
      date = timestamp.toDate();
    } else if (typeof timestamp === "number") {
      date = new Date(timestamp);
    } else if (typeof timestamp === "string") {
      date = new Date(timestamp);
    } else if (typeof timestamp === "object" && timestamp?.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      return "—";
    }

    if (Number.isNaN(date.getTime())) {
      return "—";
    }

    return date.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (error) {
    console.error("Error formatting timestamp", timestamp, error);
    return "—";
  }
}

export default PendingBookingsList;
